import { jsPDF } from "jspdf";

const PDF_MARGIN = 56;
const PDF_BORDER_INSET = 24;
const PDF_LINE = 1;
const PDF_SECTION_GAP = 24;
const PDF_BLOCK_GAP = 15;
const PDF_LINE_HEIGHT = 18;
const PDF_LINE_HEIGHT_SMALL = 15;
const LOGO_MAX_WIDTH = 150;
const LOGO_MAX_HEIGHT = 36;

const round1 = (n) => Math.round(n * 10) / 10;
const round0 = (n) => Math.round(n);

function formatMoney(n, currency) {
  const val = Number.isFinite(n) ? n : 0;
  const cur = currency || "USD";
  return `${cur} ${Math.round(val).toLocaleString()}`;
}

function loadImageAsDataUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      c.getContext("2d").drawImage(img, 0, 0);
      resolve(c.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = url;
  });
}

function drawPageBorder(doc) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.setDrawColor(180);
  doc.setLineWidth(0.5);
  doc.rect(
    PDF_BORDER_INSET,
    PDF_BORDER_INSET,
    w - PDF_BORDER_INSET * 2,
    h - PDF_BORDER_INSET * 2,
  );
}

function drawLogo(doc, logoDataUrl) {
  if (!logoDataUrl) return;
  doc.addImage(
    logoDataUrl,
    "PNG",
    PDF_MARGIN,
    38,
    LOGO_MAX_WIDTH,
    LOGO_MAX_HEIGHT,
  );
}

function addPdfHeader(doc, title, subtitle, hasLogo) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const titleX = hasLogo ? PDF_MARGIN + LOGO_MAX_WIDTH + 20 : PDF_MARGIN;

  doc.setFontSize(20);
  doc.text(title, titleX, 52);

  doc.setFontSize(8);
  doc.text(subtitle, titleX, 76);

  doc.setLineWidth(PDF_LINE);
  doc.line(PDF_MARGIN, 98, pageWidth - PDF_MARGIN, 98);
}

function pdfEnsureSpace(doc, y, needed, top, bottom, drawBorder) {
  const h = doc.internal.pageSize.getHeight();
  if (y + needed > h - bottom) {
    doc.addPage();
    drawBorder(doc);
    return top;
  }
  return y;
}

function pdfWriteBlockAuto(doc, text, x, y, maxWidth, opts = {}) {
  const {
    fontSize = 11,
    lineHeight = PDF_LINE_HEIGHT,
    maxLines = Infinity,
    top = 40,
    bottom = 50,
    drawBorder,
  } = opts;

  doc.setFontSize(fontSize);
  let lines = doc.splitTextToSize(text, maxWidth);

  if (lines.length > maxLines) {
    lines = lines.slice(0, maxLines);
    while (doc.getTextWidth(lines[maxLines - 1] + "…") > maxWidth) {
      lines[maxLines - 1] = lines[maxLines - 1].slice(0, -1);
    }
    lines[maxLines - 1] += "…";
  }

  const needed = lines.length * lineHeight;
  y = pdfEnsureSpace(doc, y, needed, top, bottom, drawBorder);

  for (const l of lines) {
    doc.text(l, x, y);
    y += lineHeight;
  }
  return y;
}

function pdfSectionTitle(doc, title, x, y) {
  doc.setFontSize(14);
  doc.text(title, x, y);
  return y + PDF_SECTION_GAP;
}

export async function generateJobAutomationPdf(safeAnalysis, options = {}) {
  const logoUrl =
    options.logoUrl || (process.env.PUBLIC_URL || "") + "/logo.png";
  let logoDataUrl = null;
  try {
    logoDataUrl = await loadImageAsDataUrl(logoUrl);
  } catch {}

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const maxWidth = pageWidth - PDF_MARGIN * 2;
  const drawBorder = (d) => drawPageBorder(d);

  drawBorder(doc);
  drawLogo(doc, logoDataUrl);

  const now = new Date();
  const stamp = now.toLocaleString();
  const s = safeAnalysis.summary;
  const savings = s.savings;
  const distribution = s.distribution || { High: 0, Medium: 0, Low: 0 };

  addPdfHeader(
    doc,
    "Job Automation Report",
    "Summary of tasks, automation potential, savings, and recommended automations",
    !!logoDataUrl,
  );

  let y = 118;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);

  y =
    pdfWriteBlockAuto(
      doc,
      [
        s.role ? `Role: ${s.role}` : "Role: (not provided)",
        `Generated: ${stamp}`,
        `Tasks: ${s.tasksCount}`,
      ].join("\n"),
      PDF_MARGIN,
      y,
      maxWidth,
      { drawBorder },
    ) + 12;

  y = pdfSectionTitle(doc, "1) Executive Summary", PDF_MARGIN, y);
  doc.setFont("helvetica", "normal");

  const exec =
    `Based on the tasks provided, the estimated automation potential for this role is ${round0(s.overallAutomation)}%. ` +
    `This score reflects repeatability, tool readiness, decision complexity, and communication load. ` +
    `Below you will find a task-by-task breakdown, estimated time and cost savings, and a recommended automation plan.`;

  y =
    pdfWriteBlockAuto(doc, exec, PDF_MARGIN, y, maxWidth, {
      drawBorder,
    }) + PDF_BLOCK_GAP;

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Key Drivers", PDF_MARGIN, y);
  doc.setFont("helvetica", "normal");
  y += 22;

  doc.setFontSize(10);
  y = pdfWriteBlockAuto(
    doc,
    `Repeatability: ${round0(s.drivers.repeatability)}%`,
    PDF_MARGIN + 12,
    y,
    maxWidth,
    { drawBorder },
  );
  y = pdfWriteBlockAuto(
    doc,
    `Decision Complexity: ${round0(s.drivers.decisionComplexity)}%`,
    PDF_MARGIN + 12,
    y,
    maxWidth,
    { drawBorder },
  );
  y = pdfWriteBlockAuto(
    doc,
    `Communication Load: ${round0(s.drivers.communicationLoad)}%`,
    PDF_MARGIN + 12,
    y,
    maxWidth,
    { drawBorder },
  );
  y =
    pdfWriteBlockAuto(
      doc,
      `Tool Readiness: ${round0(s.drivers.toolReadiness)}%`,
      PDF_MARGIN + 12,
      y,
      maxWidth,
      { drawBorder },
    ) + PDF_SECTION_GAP;

  doc.setFont("helvetica", "bold");
  y = pdfSectionTitle(doc, "2) Overall Estimated Savings", PDF_MARGIN, y);
  doc.setFont("helvetica", "normal");

  const hoursLine =
    savings.hoursPerWeek > 0
      ? `Input workload: ${round1(savings.hoursPerWeek)} hrs/week. Estimated hours saved: ${round1(savings.weeklyHoursSaved)} hrs/week.`
      : "Input workload: not provided. Add hours/week to calculate time savings.";

  y =
    pdfWriteBlockAuto(doc, hoursLine, PDF_MARGIN, y, maxWidth, {
      drawBorder,
    }) + PDF_BLOCK_GAP;

  const valueLine =
    savings.hourlyCost > 0 && savings.hoursPerWeek > 0
      ? `Estimated value saved: ${formatMoney(savings.weeklyValue, savings.currency)}/week, ${formatMoney(savings.monthlyValue, savings.currency)}/month, ${formatMoney(savings.yearlyValue, savings.currency)}/year (based on your hourly cost).`
      : "Add an hourly cost to calculate monetary savings.";

  y =
    pdfWriteBlockAuto(doc, valueLine, PDF_MARGIN, y, maxWidth, {
      drawBorder,
    }) + PDF_SECTION_GAP;
  doc.setFont("helvetica", "bold");

  doc.setFontSize(12);
  doc.text("Automation Distribution", PDF_MARGIN, y);
  y += 22;
  doc.setFont("helvetica", "normal");

  doc.setFontSize(10);
  y = pdfWriteBlockAuto(
    doc,
    `High: ${distribution.High} tasks`,
    PDF_MARGIN + 12,
    y,
    maxWidth,
    { drawBorder },
  );
  y = pdfWriteBlockAuto(
    doc,
    `Medium: ${distribution.Medium} tasks`,
    PDF_MARGIN + 12,
    y,
    maxWidth,
    { drawBorder },
  );
  y =
    pdfWriteBlockAuto(
      doc,
      `Low: ${distribution.Low} tasks`,
      PDF_MARGIN + 12,
      y,
      maxWidth,
      { drawBorder },
    ) + PDF_SECTION_GAP;
  doc.setFont("helvetica", "bold");
  doc.addPage();
  drawBorder(doc);
  y = 72;
  y = pdfSectionTitle(
    doc,
    "3) Task Breakdown and Automation Potential",
    PDF_MARGIN,
    y,
  );
  doc.setFont("helvetica", "normal");

  const col1 = PDF_MARGIN;
  const col2 = pageWidth - 170;
  const col3 = pageWidth - 95;

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Task", col1, y);
  doc.text("Automation", col2, y);
  doc.text("Band", col3, y);
  y += 22;
  doc.setFont("helvetica", "normal");

  for (const r of safeAnalysis.taskTable.slice(0, 18)) {
    const startY = y;
    y = pdfWriteBlockAuto(doc, r.task, col1, y, col2 - col1 - 16, {
      fontSize: 10,
      lineHeight: PDF_LINE_HEIGHT_SMALL,
      maxLines: 3,
      drawBorder,
    });
    doc.text(`${r.automationPotential}%`, col2, startY);
    doc.text(r.band, col3, startY);
    y += 16;
  }

  doc.setFont("helvetica", "bold");
  y = pdfSectionTitle(doc, "4) Recommended Automation Plan", PDF_MARGIN, y);
  doc.setFont("helvetica", "normal");

  for (const p of safeAnalysis.recommendedAutomation.plan) {
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(p.title, PDF_MARGIN, y);
    doc.setFont("helvetica", "normal");
    y += 16;

    y =
      pdfWriteBlockAuto(
        doc,
        `Why: ${p.why}`,
        PDF_MARGIN + 12,
        y,
        maxWidth - 12,
        {
          fontSize: 10,
          lineHeight: PDF_LINE_HEIGHT_SMALL,
          drawBorder,
        },
      ) + PDF_BLOCK_GAP;

    y =
      pdfWriteBlockAuto(doc, "What:", PDF_MARGIN + 12, y, maxWidth - 12, {
        fontSize: 10,
        lineHeight: PDF_LINE_HEIGHT_SMALL,
        drawBorder,
      }) + 6;

    for (const w of p.what || []) {
      y = pdfWriteBlockAuto(doc, `• ${w}`, PDF_MARGIN + 24, y, maxWidth - 24, {
        fontSize: 10,
        lineHeight: PDF_LINE_HEIGHT_SMALL,
        drawBorder,
      });
    }

    y =
      pdfWriteBlockAuto(
        doc,
        `Best for: ${p.bestFor}`,
        PDF_MARGIN + 12,
        y + PDF_BLOCK_GAP,
        maxWidth - 12,
        {
          fontSize: 10,
          lineHeight: PDF_LINE_HEIGHT_SMALL,
          drawBorder,
        },
      ) + PDF_SECTION_GAP;
  }
  doc.setFont("helvetica", "bold");

  y = pdfSectionTitle(doc, "5) Get in Touch", PDF_MARGIN, y);
  doc.setFont("helvetica", "normal");

  y =
    pdfWriteBlockAuto(
      doc,
      "If you want this turned into real automations, the next step is a short discovery call where we pick the highest-impact tasks and map your tools and data.",
      PDF_MARGIN,
      y,
      maxWidth,
      { drawBorder },
    ) + PDF_SECTION_GAP;

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text(`Contact: support@aiagencydirectory.com`, PDF_MARGIN, y - 20);
  doc.setFont("helvetica", "normal");

  doc.setFontSize(10);
  for (const ns of safeAnalysis.callToAction.nextSteps) {
    y = pdfWriteBlockAuto(doc, `• ${ns}`, PDF_MARGIN + 12, y, maxWidth, {
      fontSize: 10,
      lineHeight: PDF_LINE_HEIGHT_SMALL,
      drawBorder,
    });
  }

  doc.save("job-automation-report.pdf");
}
